import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Category } from '../../types/category.type';
import { CATEGORY_NAME_MAX_LENGTH, CATEGORY_NAME_MIN_LENGTH } from '../../constants/category-name-length.constants';
import { UpdateCategoryRequestDTO } from '../../types/dtos/update-category.dto';

const CATEGORY_NAME_INPUT_ID = 'edit-category-name';
const CATEGORY_DESCRIPTION_INPUT_ID = 'edit-category-description';

type Props = {
  open: boolean;
  category: Category;
  isSubmitting: boolean;
  onSubmit: (data: UpdateCategoryRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    minWidth: 400,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function EditCategoryDialog({ open, category, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();

  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const changes: UpdateCategoryRequestDTO = {};
  if (trimmedName !== category.name) changes.name = trimmedName;
  if (trimmedDescription !== category.description) changes.description = trimmedDescription;

  const hasChanges = Object.keys(changes).length > 0;
  const isValid = trimmedName.length >= CATEGORY_NAME_MIN_LENGTH && trimmedDescription !== '';
  const canSubmit = hasChanges && isValid && !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(changes);
  };

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} disableEscapeKeyDown={isSubmitting}>
      <DialogTitle>Modifier la rubrique</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={CATEGORY_NAME_INPUT_ID}
            label="Nom"
            inputProps={{ maxLength: CATEGORY_NAME_MAX_LENGTH }}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            multiline
            minRows={3}
            id={CATEGORY_DESCRIPTION_INPUT_ID}
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
